import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, UserPlus, FileSignature, FileCheck2, Clock } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getStoredUser, prospects } from '../api';
import { useToast } from '../hooks/use-toast';
import { ProspectDialog } from '../components/prospects/ProspectDialog';
import { TemplatesDialog } from '../components/prospects/TemplatesDialog';
import { STAGES, zar, daysSince, errMsg } from '../components/prospects/constants';

const Card = ({ p, onOpen }) => (
  <div draggable onDragStart={(e) => e.dataTransfer.setData('text/plain', p.id)} onClick={() => onOpen(p)}
    className="group rounded-lg bg-white border border-stone-200 p-3 cursor-pointer hover:border-emerald-700 hover:shadow-md transition-[border-color,box-shadow]" data-testid={`prospect-card-${p.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="font-semibold text-sm text-stone-900 leading-tight">{p.name}</div>
      {p.ready && p.stage !== 'converted' && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800" data-testid={`prospect-ready-${p.id}`}>Ready</span>}
    </div>
    <div className="text-[11px] text-stone-500 mt-0.5">{p.entityType}{p.contact ? ` · ${p.contact}` : ''}</div>
    <div className="mt-2 text-sm font-semibold text-emerald-900 tabular-nums">{zar(p.monthlyFee)}<span className="text-[11px] font-normal text-stone-500">/mo</span></div>
    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-stone-600">
      {p.progress.docsTotal > 0 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100"><FileSignature size={11}/>{p.progress.docsSigned}/{p.progress.docsTotal} signed</span>}
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100"><FileCheck2 size={11}/>FICA {p.progress.ficaDone}/{p.progress.ficaRequired}</span>
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100"><Clock size={11}/>{daysSince(p.stageChangedAt)}d</span>
    </div>
  </div>
);

const Column = ({ stage, items, onDrop, onOpen }) => {
  const [over, setOver] = useState(false);
  const total = items.reduce((a, p) => a + (p.monthlyFee || 0), 0);
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData('text/plain'), stage.key); }}
      className={`w-64 shrink-0 rounded-xl border p-2.5 flex flex-col transition-colors ${over ? 'bg-emerald-50 border-emerald-300' : 'bg-stone-100/70 border-stone-200'}`} data-testid={`prospect-column-${stage.key}`}>
      <div className="flex items-center gap-2 px-1 pb-2">
        <span className={`h-2 w-2 rounded-full ${stage.dot}`}/>
        <span className="text-xs font-semibold uppercase tracking-wider text-stone-700">{stage.label}</span>
        <span className="text-xs text-stone-500">{items.length}</span>
        {total > 0 && <span className="ml-auto text-[11px] text-stone-500 tabular-nums">{zar(total)}</span>}
      </div>
      <div className="space-y-2 min-h-[80px]">{items.map((p) => <Card key={p.id} p={p} onOpen={onOpen}/>)}</div>
    </div>
  );
};

export default function ProspectsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user] = useState(getStoredUser());
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [showLost, setShowLost] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [tpl, setTpl] = useState(false);
  const load = () => prospects.list().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (list || []).filter((p) => !q || `${p.name} ${p.contact} ${p.email}`.toLowerCase().includes(q.toLowerCase())), [list, q]);
  const open = filtered.filter((p) => !['converted', 'lost'].includes(p.stage));
  const converted = (list || []).filter((p) => p.stage === 'converted').length;
  const closed = converted + (list || []).filter((p) => p.stage === 'lost').length;

  const move = (id, stage) => {
    const p = list.find((x) => x.id === id);
    if (!p || p.stage === stage) return;
    if (stage === 'converted') { navigate(`/dashboard/prospects/${id}`); toast({ title: 'Use "Convert to client"', description: 'Converting creates the client, tasks and portal.' }); return; }
    const reason = stage === 'lost' ? window.prompt('Why was this prospect lost? (optional)') : '';
    if (reason === null) return;
    setList((l) => l.map((x) => (x.id === id ? { ...x, stage } : x)));
    prospects.stage(id, stage, reason || '').then(load).catch((e) => { toast({ title: 'Could not move', description: errMsg(e), variant: 'destructive' }); load(); });
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Prospects"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2"><UserPlus size={24} className="text-emerald-800"/> Prospects</h1>
              <p className="mt-1 text-stone-600 text-sm" data-testid="prospects-summary">
                {list ? `${open.length} in pipeline · ${zar(open.reduce((a, p) => a + (p.monthlyFee || 0), 0))}/mo potential · ${closed ? Math.round((converted / closed) * 100) : 0}% won` : 'Track every prospect from first contact to paying client'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prospects…" className="h-10 w-56 bg-white" data-testid="prospects-search-input"/>
              <Button variant="outline" onClick={() => setShowLost((s) => !s)} className="h-10" data-testid="prospects-toggle-closed-btn">{showLost ? 'Hide' : 'Show'} converted & lost</Button>
              <Button variant="outline" onClick={() => setTpl(true)} className="h-10" data-testid="prospects-templates-btn"><FileSignature size={15} className="mr-2"/> Templates</Button>
              <Button onClick={() => setDialog(true)} className="h-10 bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="prospects-new-btn"><Plus size={15} className="mr-2"/> New prospect</Button>
            </div>
          </div>
          {!list ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div> : (
            <div className="flex gap-3 overflow-x-auto pb-4" data-testid="prospects-board">
              {STAGES.filter((s) => showLost || !['converted', 'lost'].includes(s.key)).map((s) => (
                <Column key={s.key} stage={s} items={filtered.filter((p) => p.stage === s.key)} onDrop={move} onOpen={(p) => navigate(`/dashboard/prospects/${p.id}`)}/>
              ))}
            </div>
          )}
          {list && list.length === 0 && <p className="text-sm text-stone-500" data-testid="prospects-empty">No prospects yet. Add your first one to start the onboarding journey.</p>}
        </main>
      </div>
      <ProspectDialog open={dialog} onOpenChange={setDialog} onSaved={(p) => navigate(`/dashboard/prospects/${p.id}`)} toast={toast}/>
      <TemplatesDialog open={tpl} onOpenChange={setTpl} toast={toast}/>
    </div>
  );
}
